import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarDays, RefreshCw, Search, UserRound } from "lucide-react";
import api from "../../services/api";
import { formatPlannedMinutes, resolvePlannedMinutes } from "../../utils/plannedMinutes";

const DAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const CAPACITY_TONE = {
  ok: {
    label: "Ruimte",
    pill: "border-emerald-200 bg-emerald-50 text-emerald-700",
    bar: "bg-emerald-500",
    text: "text-emerald-700",
    card: "border-emerald-100",
  },
  warning: {
    label: "Bijna vol",
    pill: "border-amber-200 bg-amber-50 text-amber-700",
    bar: "bg-amber-500",
    text: "text-amber-700",
    card: "border-amber-100",
  },
  over: {
    label: "Overpland",
    pill: "border-rose-200 bg-rose-50 text-rose-700",
    bar: "bg-rose-500",
    text: "text-rose-700",
    card: "border-rose-100",
  },
};

export default function InzetCapaciteitPage() {
  const [werkbonnen, setWerkbonnen] = useState([]);
  const [monteurs, setMonteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => toISODate(new Date()));
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedMonteurId, setSelectedMonteurId] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        setLoading(true);
        setError("");
        const [werkbonRes, monteurRes] = await Promise.all([
          api.get("/werkbonnen"),
          api.get("/users", { params: { role: "Monteur" } }),
        ]);
        if (!mounted) return;
        setWerkbonnen(Array.isArray(werkbonRes.data) ? werkbonRes.data : []);
        setMonteurs(asArray(monteurRes.data));
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || "Inzet laden mislukt");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  const weekDates = useMemo(() => buildWeek(selectedDate), [selectedDate]);
  const weekDateSet = useMemo(() => new Set(weekDates.map((date) => date.iso)), [weekDates]);
  const weekNumber = useMemo(() => getWeekNumber(selectedDate), [selectedDate]);
  const normalizedQuery = query.trim().toLowerCase();
  const normalizedWerkbonnen = useMemo(() => werkbonnen.map((item) => normalizeWerkbon(item)), [werkbonnen]);
  const selectedMonteur = useMemo(
    () => monteurs.find((item) => String(item._id) === String(selectedMonteurId)) || null,
    [monteurs, selectedMonteurId]
  );

  const capacityRows = useMemo(() => {
    const sourceMonteurs = monteurs.length > 0 ? monteurs : [];
    return sourceMonteurs
      .map((monteur) => {
        const monteurId = String(monteur?._id || "");
        const weeklyAssignments = normalizedWerkbonnen
          .filter(
            (item) =>
              monteurId &&
              item.assignedUserIds.includes(monteurId) &&
              item.plannedDate &&
              weekDateSet.has(item.plannedDate) &&
              item.statusKey !== "afgerond"
          )
          .sort(sortByDateThenTime);
        const plannedHours = weeklyAssignments.reduce((sum, item) => sum + item.durationHours, 0);
        const maxHours = Number(monteur?.technicianProfile?.maxHoursPerWeek ?? 40);
        const remainingHours = Number((maxHours - plannedHours).toFixed(1));
        const utilization = maxHours > 0 ? plannedHours / maxHours : plannedHours > 0 ? 1 : 0;
        return {
          monteur,
          weeklyAssignments,
          plannedHours: Number(plannedHours.toFixed(1)),
          maxHours,
          remainingHours,
          utilization,
          status: getCapacityStatus(utilization, remainingHours),
        };
      })
      .filter((row) => {
        if (statusFilter !== "all" && row.status !== statusFilter) return false;
        if (selectedMonteurId && String(row.monteur?._id) !== String(selectedMonteurId)) return false;
        if (!normalizedQuery) return true;
        return [row.monteur?.naam, row.monteur?.email, row.weeklyAssignments.length, row.plannedHours]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        const severity = { over: 0, warning: 1, ok: 2 };
        if (severity[a.status] !== severity[b.status]) return severity[a.status] - severity[b.status];
        return b.plannedHours - a.plannedHours;
      });
  }, [monteurs, normalizedQuery, normalizedWerkbonnen, selectedMonteurId, statusFilter, weekDateSet]);

  const totals = useMemo(() => {
    const plannedHours = capacityRows.reduce((sum, item) => sum + item.plannedHours, 0);
    const maxHours = capacityRows.reduce((sum, item) => sum + item.maxHours, 0);
    return {
      monteurs: capacityRows.length,
      plannedHours: Number(plannedHours.toFixed(1)),
      maxHours: Number(maxHours.toFixed(1)),
      overplanned: capacityRows.filter((item) => item.status === "over").length,
    };
  }, [capacityRows]);

  return (
    <section className="space-y-6">
      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(191,219,254,0.6),_transparent_32%),linear-gradient(135deg,_#0f172a,_#1e293b_55%,_#334155)] p-6 text-white shadow-xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sky-200">
              Inzet en Capaciteit
            </p>
            <h2 className="text-3xl font-semibold tracking-tight">
              Week {weekNumber}: wie heeft ruimte en wie loopt vol.
            </h2>
            <p className="max-w-2xl text-sm text-slate-200">
              Apart van planning, zodat je werk kunt verdelen zonder het planbord vol te drukken.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:min-w-[420px] lg:grid-cols-4">
            <MetricCard label="Monteurs" value={totals.monteurs} icon={UserRound} />
            <MetricCard label="Uren ingepland" value={totals.plannedHours} icon={CalendarDays} />
            <MetricCard label="Weekcapaciteit" value={totals.maxHours} icon={RefreshCw} />
            <MetricCard label="Overpland" value={totals.overplanned} icon={AlertTriangle} />
          </div>
        </div>
      </div>

      <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Weekfocus</span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Zoeken</span>
              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Monteur..."
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </div>
            </label>
          </div>

          <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
            {weekDates.map((date) => {
              const active = date.iso === selectedDate;
              return (
                <button
                  key={date.iso}
                  type="button"
                  onClick={() => setSelectedDate(date.iso)}
                  className={`rounded-2xl px-3 py-2 text-left transition ${
                    active ? "bg-slate-900 text-white shadow" : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <div className="text-xs font-medium">{date.dayName}</div>
                  <div className="text-sm font-semibold">{date.dayNumber}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {[
              { key: "all", label: "Alles" },
              { key: "warning", label: "Bijna vol" },
              { key: "over", label: "Overpland" },
            ].map((option) => {
              const active = statusFilter === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setStatusFilter(option.key)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                    active
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            {selectedMonteurId ? (
              <>
                <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 font-medium text-sky-700">
                  Filter op {selectedMonteur?.naam || "monteur"}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedMonteurId("")}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                >
                  Toon alle monteurs
                </button>
              </>
            ) : (
              <span className="text-slate-500">Klik op een monteurkaart om in te zoomen.</span>
            )}
          </div>
        </div>
      </section>

      {loading && (
        <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          Inzet laden...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow-sm">
          {error}
        </div>
      )}

      {!loading && !error && (
        <section className="grid gap-4 xl:grid-cols-2">
          {capacityRows.map((item) => (
            <CapacityCard
              key={String(item.monteur._id)}
              item={item}
              active={String(item.monteur._id) === String(selectedMonteurId)}
              onSelect={() =>
                setSelectedMonteurId((prev) =>
                  String(prev) === String(item.monteur._id) ? "" : String(item.monteur._id)
                )
              }
            />
          ))}
          {capacityRows.length === 0 && (
            <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
              Geen monteurs of geen inzet gevonden in deze week.
            </div>
          )}
        </section>
      )}
    </section>
  );
}

function MetricCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-[20px] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.22em] text-slate-200">{label}</span>
        <Icon size={16} className="text-sky-200" />
      </div>
      <p className="mt-3 text-3xl font-semibold">{value}</p>
    </div>
  );
}

function CapacityCard({ item, active = false, onSelect }) {
  const tone = CAPACITY_TONE[item.status] || CAPACITY_TONE.ok;
  return (
    <article
      className={`cursor-pointer rounded-[24px] border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        active ? "border-slate-900 ring-2 ring-slate-900/10" : tone.card
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-950">{item.monteur?.naam || "Monteur"}</h3>
          <p className="text-sm text-slate-500">{item.monteur?.email || "-"}</p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${tone.pill}`}>
          {tone.label}
        </span>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-sm font-medium text-slate-700">
          <span>{item.plannedHours.toFixed(1)} uur ingepland</span>
          <span>{item.maxHours.toFixed(1)} uur beschikbaar</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
          <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${Math.max(4, Math.min(item.utilization * 100, 100))}%` }} />
        </div>
        <p className={`mt-3 text-sm font-medium ${tone.text}`}>
          {item.remainingHours >= 0
            ? `${item.remainingHours.toFixed(1)} uur vrij`
            : `${Math.abs(item.remainingHours).toFixed(1)} uur over capaciteit`}
        </p>
      </div>

      <div className="mt-4 rounded-2xl bg-slate-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Werkbonnen deze week</p>
        <ul className="mt-3 space-y-2">
          {item.weeklyAssignments.slice(0, 5).map((assignment) => (
            <li key={assignment.id} className="flex items-start justify-between gap-3 rounded-2xl bg-white px-3 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{assignment.title}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {formatShortDate(assignment.plannedDate)} {assignment.startTime || ""}
                </p>
              </div>
              <span className="shrink-0 text-xs font-semibold text-slate-500">{assignment.durationLabel}</span>
            </li>
          ))}
          {item.weeklyAssignments.length === 0 && <li className="text-sm text-slate-500">Geen werkbonnen ingepland.</li>}
        </ul>
      </div>
    </article>
  );
}

function normalizeWerkbon(item) {
  const assignedUsers = Array.isArray(item?.assignedUsers) ? item.assignedUsers : [];
  const teamUsers = Array.isArray(item?.team) ? item.team : [];
  const assignedUserIds = [item?.assignedTo, ...assignedUsers, ...teamUsers.map((member) => member?.userId || member?._id || member)]
    .filter(Boolean)
    .map((value) => String(typeof value === "object" ? value?._id || value?.id || value : value));

  return {
    id: String(item?._id || item?.id || Math.random().toString(36).slice(2, 10)),
    title: String(item?.omschrijving || item?.opdracht?.omschrijving || "Werkbon zonder titel"),
    plannedDate: normalizeDateString(item?.plannedDate || item?.plannedStartAt),
    startTime: String(item?.plannedStartTime || "").trim(),
    durationHours: estimateDurationHours(item),
    durationLabel: formatPlannedMinutes(resolvePlannedMinutes(item)),
    assignedUserIds,
    statusKey: String(item?.status || "").trim().toLowerCase() || "aangemaakt",
  };
}

function estimateDurationHours(item) {
  const minutes = resolvePlannedMinutes(item);
  return Number((minutes / 60).toFixed(2));
}

function normalizeDateString(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return toISODate(parsed);
}

function buildWeek(selectedDate) {
  const base = selectedDate ? new Date(`${selectedDate}T12:00:00`) : new Date();
  const day = base.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  const monday = new Date(base);
  monday.setDate(base.getDate() + offset);

  return DAYS.map((dayName, index) => {
    const current = new Date(monday);
    current.setDate(monday.getDate() + index);
    return { dayName, dayNumber: String(current.getDate()).padStart(2, "0"), iso: toISODate(current) };
  });
}

function getWeekNumber(selectedDate) {
  const date = selectedDate ? new Date(`${selectedDate}T12:00:00`) : new Date();
  const current = new Date(date.getTime());
  current.setHours(0, 0, 0, 0);
  current.setDate(current.getDate() + 4 - (current.getDay() || 7));
  const yearStart = new Date(current.getFullYear(), 0, 1);
  return Math.ceil((((current - yearStart) / 86400000) + 1) / 7);
}

function getCapacityStatus(utilization, remainingHours) {
  if (remainingHours < 0 || utilization > 1) return "over";
  if (utilization >= 0.85) return "warning";
  return "ok";
}

function sortByDateThenTime(a, b) {
  const dateCompare = String(a.plannedDate || "").localeCompare(String(b.plannedDate || ""));
  if (dateCompare !== 0) return dateCompare;
  return String(a.startTime || "99:99").localeCompare(String(b.startTime || "99:99"));
}

function toISODate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatShortDate(value) {
  const date = new Date(`${value}T12:00:00`);
  return date.toLocaleDateString("nl-NL", { day: "2-digit", month: "short" });
}

function asArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  return [];
}
